import { CellContext, Column, createColumnHelper } from '@tanstack/react-table';
import { CheckCircle, XCircle } from 'lucide-react';

import Podman from '@/assets/ico/vendor/podman.svg?c';
import Docker from '@/assets/ico/vendor/docker.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import { getValueAsArrayOfStrings } from '@/portainer/helpers/array';
import { grammaticallyJoin, pluralize } from '@/react/common/string-utils';

import { Badge } from '@@/Badge';
import { Link } from '@@/Link';
import { MultipleSelectionFilter } from '@@/datatables/Filter';
import { Icon } from '@@/Icon';
import { Tooltip } from '@@/Tip/Tooltip';
import { badgeTypeClasses } from '@@/Badge/Badge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { StackedStatusBar } from '@@/StackedStatusBar';

import {
  PolicyType,
  getPolicyTypeDisplayName,
  getEnvironmentTypeDisplayName,
} from '../../types';
import { PolicyTypeBadge } from '../../components/PolicyTypeBadge';

import { EnrichedPolicy, PolicyRollout } from './types';

const columnHelper = createColumnHelper<EnrichedPolicy>();

export const columns = [
  columnHelper.accessor('Name', {
    header: 'Name',
    cell: NameCell,
  }),
  columnHelper.accessor('EnvironmentTypes', {
    header: () => (
      <>
        Environment types
        <Tooltip message="The environment types that the policy supports." />
      </>
    ),
    cell: EnvironmentCell,
  }),
  columnHelper.accessor('Type', {
    header: 'Type',
    cell: TypeCell,
    enableColumnFilter: true,
    filterFn: ({ original: policy }, columnId, filterValue: Array<string>) => {
      if (filterValue.length === 0) {
        return true;
      }

      const displayName = getPolicyTypeDisplayName(policy.Type);
      return filterValue.includes(displayName);
    },
    meta: {
      filter: TypeFilter,
    },
  }),
  columnHelper.accessor(
    (row) => row.environmentGroups.map((group) => group.name),
    {
      id: 'attachedTo',
      header: 'Environment groups attached',
      cell: (props) => (
        <AttachedToCell
          environmentGroups={props.row.original.environmentGroups}
        />
      ),
      sortingFn: (rowA, rowB) =>
        compareEnvironmentGroups(
          rowA.original.environmentGroups,
          rowB.original.environmentGroups
        ),
    }
  ),
  columnHelper.accessor((row) => row.status, {
    id: 'status',
    header: 'Status',
    cell: StatusCell,
  }),
  // Last updated timestamp
  columnHelper.accessor('UpdatedAt', {
    header: 'Last updated',
    cell: LastUpdatedCell,
  }),
];

function NameCell({ getValue, row }: CellContext<EnrichedPolicy, string>) {
  const name = getValue();
  const policy = row.original;

  return (
    <Link
      to="portainer.policy"
      params={{ id: policy.Id }}
      data-cy={`policy-link-${name}`}
    >
      {name}
    </Link>
  );
}

function LastUpdatedCell({ getValue }: CellContext<EnrichedPolicy, string>) {
  const updatedAt = getValue();
  // UpdatedAt comes from the API as a string timestamp; fallback if missing
  if (!updatedAt) {
    return <span className="text-muted">Unknown</span>;
  }

  let formatted: string;
  try {
    const date = new Date(updatedAt);
    formatted = Number.isNaN(date.getTime())
      ? 'Unknown'
      : date.toLocaleString();
  } catch {
    formatted = 'Unknown';
  }

  return <span>{formatted}</span>;
}

function getEnvironmentTypeIcon(environmentType: string) {
  switch (environmentType) {
    case 'kubernetes':
      return Kubernetes;
    case 'podman':
      return Podman;
    case 'docker':
    case 'swarm':
    default:
      return Docker;
  }
}

function EnvironmentCell({
  getValue,
}: CellContext<EnrichedPolicy, EnrichedPolicy['EnvironmentTypes']>) {
  const environmentTypes = getValue();
  const maxBadges = 1;
  const visibleTypes = environmentTypes.slice(0, maxBadges);
  const extraCount = environmentTypes.length - maxBadges;
  const allHiddenTypes = environmentTypes
    .slice(maxBadges)
    .map((envType) => getEnvironmentTypeDisplayName(envType));

  return (
    <div className="flex flex-wrap gap-1">
      {visibleTypes.map((envType) => {
        const EnvIcon = getEnvironmentTypeIcon(envType);
        return (
          <Badge key={envType} type="muted" className="min-w-max">
            <EnvIcon className="h-4 w-4" />
            {getEnvironmentTypeDisplayName(envType)}
          </Badge>
        );
      })}
      {extraCount > 0 && (
        <TooltipWithChildren message={grammaticallyJoin(allHiddenTypes)}>
          <span>
            <Badge type="muted" className="min-w-max cursor-default">
              + {extraCount} more
            </Badge>
          </span>
        </TooltipWithChildren>
      )}
    </div>
  );
}

function TypeCell({
  row: { original: policy },
}: CellContext<EnrichedPolicy, PolicyType>) {
  return <PolicyTypeBadge policyType={policy.Type} />;
}

function AttachedToCell({
  environmentGroups,
}: {
  environmentGroups: EnrichedPolicy['environmentGroups'];
}) {
  const maxBadges = 5;
  const shortenedBadgeValues = environmentGroups.slice(0, maxBadges);
  const extraBadges = environmentGroups.length - maxBadges;

  return (
    <div className="flex max-w-md flex-wrap gap-x-2 gap-y-1">
      {shortenedBadgeValues.map((group) => (
        <Badge
          type={group.status}
          key={group.name}
          className="flex min-w-max items-center gap-1"
        >
          <Icon icon={group.status === 'info' ? CheckCircle : XCircle} />
          <Link
            to="portainer.groups.group"
            params={{ id: group.id }}
            data-cy={`environment-group-link-${group.name}`}
            className={badgeTypeClasses[group.status]}
          >
            {group.name}
          </Link>
          {group.statusMessage && (
            <Tooltip message={group.statusMessage} className="!ml-0" />
          )}
        </Badge>
      ))}
      {extraBadges > 0 && (
        <Badge type="muted" className="flex min-w-max items-center gap-1">
          + {extraBadges} more
        </Badge>
      )}
    </div>
  );
}

function formatRolloutTooltip(rollout: PolicyRollout): string {
  const parts: string[] = [];
  if (rollout.applied > 0) {
    parts.push(`${rollout.applied} active`);
  }
  if (rollout.pending > 0) {
    parts.push(`${rollout.pending} in progress`);
  }
  if (rollout.failed > 0) {
    parts.push(`${rollout.failed} failed`);
  }
  if (rollout.notSupported > 0) {
    parts.push(`${rollout.notSupported} not supported`);
  }
  return parts.join(', ');
}

function StatusCell({
  row,
}: CellContext<EnrichedPolicy, EnrichedPolicy['status']>) {
  const policy = row.original;
  const { rollout } = policy;

  const tooltipMessage = formatRolloutTooltip(rollout);

  const displayMessage =
    rollout.total === 0
      ? 'No environments targeted'
      : `${rollout.total} ${pluralize(rollout.total, 'environment')}${
          tooltipMessage ? `: ${tooltipMessage}` : ''
        }`;

  if (rollout.total === 0) {
    return <span className="text-muted">No environments targeted</span>;
  }

  return (
    <TooltipWithChildren message={displayMessage}>
      <span>
        <Link
          to="portainer.policy"
          params={{ id: policy.Id }}
          className="no-link"
          data-cy={`policy-status-link-${policy.Id}`}
          aria-label={displayMessage}
        >
          <StackedStatusBar
            className="!w-48"
            steps={[
              {
                value: rollout.applied,
                className: 'bg-success-7',
                label: 'Active',
              },
              {
                value: rollout.pending,
                className: 'bg-warning-7',
                label: 'In progress',
              },
              {
                value: rollout.failed,
                className: 'bg-error-7',
                label: 'Failed',
              },
              {
                value: rollout.notSupported,
                className: 'bg-gray-6',
                label: 'Not supported',
              },
            ]}
            total={rollout.total}
            aria-label="Policy rollout status"
          />
        </Link>
      </span>
    </TooltipWithChildren>
  );
}

const typeFilterOptions = [
  'RBAC',
  'Security',
  'Registry',
  'Setup',
  'Banner and change confirmation',
] as const;

function TypeFilter({
  column: { getFilterValue, setFilterValue, id },
}: {
  column: Column<EnrichedPolicy>;
}) {
  const value = getFilterValue();
  const valueAsArray = getValueAsArrayOfStrings(value);

  return (
    <MultipleSelectionFilter
      options={typeFilterOptions}
      filterKey={id}
      value={valueAsArray}
      onChange={setFilterValue}
      menuTitle="Filter by type"
    />
  );
}

/**
 * Compares two arrays of environment groups by their names for sorting.
 * Groups are sorted alphabetically within each array, then compared.
 */
export function compareEnvironmentGroups(
  groupsA: Array<{ name: string }>,
  groupsB: Array<{ name: string }>
): number {
  const namesA = groupsA
    .map((g) => g.name.toLowerCase())
    .sort()
    .join(',');
  const namesB = groupsB
    .map((g) => g.name.toLowerCase())
    .sort()
    .join(',');
  return namesA.localeCompare(namesB);
}
